import React from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Card,
  FormControl,
  InputLabel,
  CardContent,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import * as MuiIcons from "@mui/icons-material";
import EditWorkOrder from "./EditWorkOrder";
import { useContext } from "react";
import { PermissionsContext } from "../../../utils/PermissionsContext";
import { useEffect } from "react";
import api, { API_BASE_URL } from "../../../utils/api";
import { Autocomplete, TextField } from "@mui/material";




/**
 * DetailsTab Component
 *
 * Displays full work order information and allows updates to:
 * - Type, Subtype, Location
 * - Status (via EditWorkOrder)
 * - Priority, Assigned User
 *
 * @param {object} props - All props for work order details view
 */
const DetailsTab = ({
  workOrderDetails,
  statuses,
  assignableUsers,
  workOrderTypes,
  workOrderSubtypes,
  workOrderLocations,
  selectedTypeId,
  selectedSubtypeId,
  selectedLocationId,
  selectedPriority,
  selectedAssignee,
  priorityLevels,
  onTypeChange,
  onSubtypeChange,
  onLocationChange,
  onAssigneeChange,
  onPriorityChange,
  calculateDaysOpen,
  setSnackbarMessage,
  setSnackbarOpen,
  setWorkOrderDetails,
  availableProperties = [],      
  handlePropertyChange, 
  imageURL,
  onPropertyChanged,
  }) => {
  const [openImageModal, setOpenImageModal] = useState(false);
  const [workOrderFiles, setWorkOrderFiles] = useState([]);
  const permissions = useContext(PermissionsContext); 
  const hasPermission = (perm) => permissions?.includes(perm);
  const [previewImage, setPreviewImage] = useState(null);
  const [isEditingProperty, setIsEditingProperty] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");




  const getStatusObject = () => {
    return statuses.find((s) => s.statusname === workOrderDetails?.status);
  };

  const getUserFullName = (userId) => {
    const user = assignableUsers.find((u) => String(u.UserID) === String(userId));
    return user ? `${user.Name} ${user.Lastname}` : "N/A";
  };
  

  const getPriorityObject = () => {
    return priorityLevels.find(
      (p) => p.priority_value === workOrderDetails?.priority
    );
  };


  useEffect(() => {
  const fetchFiles = async () => {
    if (!workOrderDetails?.order_id) return;
    try {
      const res = await api.get(`/workorder-files/${workOrderDetails.order_id}`);
      setWorkOrderFiles(res.data || []);
    } catch (err) {
      console.error("Error fetching work order files:", err);
    }
  };
  fetchFiles();
}, [workOrderDetails?.order_id]);


<Autocomplete
  size="small"
  fullWidth
  options={availableProperties || []}
  getOptionLabel={(option) =>
    option?.ClubMemberCode
      ? `${option.ClubMemberCode} - ${option.FirstName || ""} ${option.LastName || ""}`.trim()
      : ""
  }
  value={
    availableProperties.find(
      (prop) => prop.ClubMemberCode === workOrderDetails?.property_id
    ) || null
  }
  onChange={(event, newValue) => {
  if (newValue) {
    const fakeEvent = { target: { value: newValue.ClubMemberCode } };
    handlePropertyChange(fakeEvent);
    setIsEditingProperty(false);
    setSnackbarMessage("✅ Property updated successfully!");
    setSnackbarOpen(true);

    // ✅ Tell parent to refresh everything
    if (onPropertyChanged) {
      onPropertyChanged(newValue.ClubMemberCode);
    }
  }
}}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Search Member / Property"
      placeholder="Type member name or number..."
      variant="outlined"
    />
  )}
  isOptionEqualToValue={(option, value) =>
    option.ClubMemberCode === value.ClubMemberCode
  }
/>


  return (
    <Card
      sx={{
        p: 3,
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
        border: `3px solid ${getPriorityObject()?.color_code || "#ccc"}`,
      }}
    >
      <CardContent>
        {/* 🔵 Days Open */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Chip
            label={calculateDaysOpen(workOrderDetails?.created_at)}
            sx={{
              backgroundColor: "rgba(33, 150, 243, 0.1)",
              color: "#1565c0",
              fontWeight: "bold",
              fontSize: "14px",
              padding: "6px 12px",
              borderRadius: "20px",
              border: "1px solid rgba(33, 150, 243, 0.3)",
              boxShadow: "0px 2px 10px rgba(33, 150, 243, 0.1)",
            }}
          />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 3 }}>
          Work Order Details
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {imageURL && (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      mb: 3,
    }}
  >
    <Card
      elevation={3}
      sx={{
        p: 2,
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        maxWidth: "300px",
        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out",
        "&:hover": {
          transform: "scale(1.02)",
        },
      }}
      onClick={() => setOpenImageModal(true)}
    >
      <img
        src={imageURL}
        alt="Work Order Attachment"
        style={{
          width: "100%",
          height: "auto",
          borderRadius: "12px",
          border: "1px solid #ccc",
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontStyle: "italic", textAlign: "center", mt: 1, display: "block" }}
      >
        Click to enlarge
      </Typography>
    </Card>
  </Box>
)}
<Box sx={{ mb: 3 }}>
  <Card
    sx={{
      p: 2,
      borderRadius: "12px",
      
      boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
      display: "flex",
      justifyContent: "center",
    }}
  >
    <EditWorkOrder
      workOrderDetails={workOrderDetails}
      showAsButton={true}
      onUpdate={(updatedData) => {
        setWorkOrderDetails((prev) => ({ ...prev, ...updatedData }));
        setSnackbarMessage("✅ Status updated successfully!");
        setSnackbarOpen(true);
      }}
    />
  </Card>
</Box>


        <Grid container spacing={2}>
          {/* Work Order ID + Property */}
          <Grid item xs={12} sm={6}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <MuiIcons.Info color="primary" />
              <Typography variant="body2">
                <strong>Work Order ID:</strong> {`WO-${workOrderDetails?.order_id}`}
              </Typography>
            </Card>
          </Grid>

    <Grid item xs={12} sm={6}>
  <Card
    sx={{
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 1,
      justifyContent: "space-between",
      border: isEditingProperty ? "2px solid #2196f3" : "1px solid #e0e0e0",
      transition: "border 0.2s ease",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
      <MuiIcons.Home color="success" />

      {hasPermission("edit_workorder_property") ? (
        isEditingProperty ? (
          <Autocomplete
            size="small"
            fullWidth
            options={availableProperties || []}
            getOptionLabel={(option) =>
              option?.ClubMemberCode
                ? `${option.ClubMemberCode} - ${option.FirstName || ""} ${option.LastName || ""}`.trim()
                : ""
            }
            value={
              availableProperties.find(
                (prop) => prop.ClubMemberCode === workOrderDetails?.property_id
              ) || null
            }
           onChange={async (event, newValue) => {
  if (newValue) {
    const fakeEvent = { target: { value: newValue.ClubMemberCode } };

    try {
      // ✅ Wait for the backend update to finish
      await handlePropertyChange(fakeEvent);

      // ✅ Then trigger full refresh in parent
      if (onPropertyChanged) {
        await onPropertyChanged(newValue.ClubMemberCode);
      }

      setIsEditingProperty(false);
      setSnackbarMessage("✅ Property updated successfully!");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("❌ Property update failed:", err);
      setSnackbarMessage("❌ Failed to update property");
      setSnackbarOpen(true);
    }
  }
}}
            onInputChange={(event, newInputValue) => setMemberSearch(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Member / Property"
                placeholder="Type member name or number..."
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.ClubMemberCode === value.ClubMemberCode
            }
          />
        ) : (
          <Typography variant="body2">
            <strong>Property ID:</strong>{" "}
            {workOrderDetails?.property_id || "N/A"}
          </Typography>
        )
      ) : (
        <Typography variant="body2">
          <strong>Property ID:</strong>{" "}
          {workOrderDetails?.property_id || "N/A"}
        </Typography>
      )}
    </Box>

    {hasPermission("edit_workorder_property") && (
      <IconButton
        onClick={() => setIsEditingProperty((prev) => !prev)}
        size="small"
        color={isEditingProperty ? "error" : "primary"}
        title={isEditingProperty ? "Cancel edit" : "Edit property"}
      >
        {isEditingProperty ? (
          <MuiIcons.Close fontSize="small" />
        ) : (
          <MuiIcons.Edit fontSize="small" />
        )}
      </IconButton>
    )}
  </Card>
</Grid>




         {/* Type Selection */}
<Grid item xs={12} sm={6}>
  <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
    <MuiIcons.Category color="primary" />

    {hasPermission("edit_workorder_type") ? (
      <FormControl fullWidth size="small">
        <InputLabel id="type-select-label">Work Order Type</InputLabel>
        <Select
          labelId="type-select-label"
          value={selectedTypeId || ""}
          onChange={(e) => {
            onTypeChange(e);
            setSnackbarMessage("✅ Work Order Type updated!");
            setSnackbarOpen(true);
          }}
          label="Work Order Type"
        >
          {workOrderTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <Typography variant="body2">
        <strong>Type:</strong>{" "}
        {workOrderTypes.find((t) => t.id === selectedTypeId)?.type || "N/A"}
      </Typography>
    )}
  </Card>
</Grid>

{/* Subtype Selection */}
<Grid item xs={12} sm={6}>
  <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
    <MuiIcons.BuildCircle color="primary" />

    {hasPermission("edit_workorder_subtype") ? (
      <FormControl fullWidth size="small">
        <InputLabel id="subtype-label">Subtype</InputLabel>
        <Select
          labelId="subtype-label"
          value={selectedSubtypeId || ""}
          onChange={(e) => {
            onSubtypeChange(e);
            setSnackbarMessage("✅ Subtype updated!");
            setSnackbarOpen(true);
          }}
          label="Subtype"
        >
          {workOrderSubtypes.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.subtype_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <Typography variant="body2">
        <strong>Subtype:</strong>{" "}
        {workOrderSubtypes.find((s) => s.id === selectedSubtypeId)?.subtype_name || "N/A"}
      </Typography>
    )}
  </Card>
</Grid>


         {/* Location Selection */}
<Grid item xs={12} sm={6}>
  <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
    <MuiIcons.LocationOn color="secondary" />

    {hasPermission("edit_workorder_location") ? (
      <FormControl fullWidth size="small">
        <InputLabel id="location-label">Location</InputLabel>
        <Select
          labelId="location-label"
          value={selectedLocationId || ""}
          onChange={(e) => {
            onLocationChange(e);
            setSnackbarMessage("✅ Location updated!");
            setSnackbarOpen(true);
          }}
          label="Location"
        >
          {workOrderLocations.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.location_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <Typography variant="body2">
        <strong>Location:</strong>{" "}
        {workOrderLocations.find((l) => l.id === selectedLocationId)?.location_name || "N/A"}
      </Typography>
    )}
  </Card>
</Grid>


          {/* Description */}
          <Grid item xs={12}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <MuiIcons.Description color="secondary" />
              <Typography variant="body2">
                <strong>Description:</strong> {workOrderDetails.description || "No Description Available"}
              </Typography>
            </Card>
          </Grid>

          {/* Details */}
          <Grid item xs={12}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <MuiIcons.ListAlt color="secondary" />
              <Typography variant="body2">
                <strong>Details:</strong> {workOrderDetails.details || "No Description Available"}
              </Typography>
            </Card>
          </Grid>

          {/* Created At */}
          <Grid item xs={12} sm={6}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <MuiIcons.CalendarToday color="warning" />
              <Typography variant="body2">
                <strong>Created:</strong>{" "}
                {workOrderDetails.created_at
                  ? new Date(workOrderDetails.created_at).toLocaleString()
                  : "N/A"}
              </Typography>
            </Card>
          </Grid>

          {/* Status Display */}
          <Grid item xs={12} sm={6}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              {(() => {
                const statusObj = getStatusObject();
                if (!statusObj) {
                  return (
                    <>
                      <MuiIcons.Info color="disabled" />
                      <Typography variant="body2">
                        <strong>Status:</strong> Unknown
                      </Typography>
                    </>
                  );
                }

                const IconComponent = MuiIcons[statusObj.icon] || MuiIcons.Info;
                const iconColor = statusObj.color || "default";

                return (
                  <>
                    <IconComponent color={iconColor} />
                    <Typography variant="body2">
                      <strong>Status:</strong> {statusObj.statusname}
                    </Typography>
                  </>
                );
              })()}
            </Card>
          </Grid>

         {/* Assignee */}
<Grid item xs={12} sm={6}>
  <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
    <MuiIcons.Person color="info" />

    {hasPermission("edit_workorder_assignee") ? (
      // Editable Select dropdown
      <FormControl fullWidth size="small">
        <InputLabel id="assign-to-label">Assign To</InputLabel>
        <Select
          labelId="assign-to-label"
          value={selectedAssignee && selectedAssignee !== 0 ? selectedAssignee : ""}
          onChange={(e) => {
            onAssigneeChange(e);
            setSnackbarMessage("✅ Assignee updated!");
            setSnackbarOpen(true);
          }}
          label="Assign To"
        >
          {/* Option to unassign */}
          <MenuItem value={0}>Unassigned</MenuItem>

          {assignableUsers.map((user) => (
            <MenuItem key={user.UserID} value={user.UserID}>
              {user.Name} {user.Lastname}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      // Read-only fallback
      <Typography variant="body2">
        <strong>Assigned To:</strong>{" "}
        {selectedAssignee && selectedAssignee !== 0
          ? getUserFullName(selectedAssignee)
          : "Unassigned"}
      </Typography>
    )}
  </Card>
</Grid>




          {/* Priority */}
<Grid item xs={12} sm={6}>
  <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
    <MuiIcons.Flag color="error" />

    {hasPermission("edit_workorder_priority") ? (
      <FormControl fullWidth size="small">
        <InputLabel id="priority-select-label">Priority</InputLabel>
        <Select
          labelId="priority-select-label"
          value={selectedPriority || ""}
          onChange={(e) => {
            onPriorityChange(e);
            setSnackbarMessage("✅ Priority updated!");
            setSnackbarOpen(true);
          }}
          label="Priority"
        >
          {priorityLevels.map((priority) => (
            <MenuItem key={priority.priority_value} value={priority.priority_value}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  sx={{
                    backgroundColor: priority.color_code,
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                  label={priority.priority_value}
                />
                {priority.display_name}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <Typography variant="body2">
        <strong>Priority:</strong>{" "}
        {
          priorityLevels.find((p) => p.priority_value === selectedPriority)
            ?.display_name || "N/A"
        }
      </Typography>
    )}
  </Card>
</Grid>

{/* 📎 Work Order Files Section */}
<Grid item xs={12}>
  <Card
    sx={{
      p: 2,
      mt: 1,
      borderRadius: "12px",
      backgroundColor: "#fafafa",
      boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
    }}
  >
    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
      📎 Work Order Files
    </Typography>

    {workOrderFiles.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No files uploaded for this work order.
      </Typography>
    ) : (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {workOrderFiles.map((file) => {
          const filename = file?.file_url
            ? file.file_url.split(/[/\\]/).pop()
            : null;

          const fileUrl = filename
            ? `${API_BASE_URL}/uploads/${encodeURIComponent(filename)}?inline=1`
            : null;

          const downloadUrl = filename
            ? `${API_BASE_URL}/uploads/${encodeURIComponent(filename)}?inline=0`
            : null;

          const isImage = file.file_type?.includes("image");
          const isPdf = file.file_type?.includes("pdf");

          return (
            <Card
              key={file.id}
              sx={{
                width: 160,
                p: 1.5,
                border: "1px solid #ddd",
                borderRadius: "10px",
                backgroundColor: "#fff",
                boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                transition: "transform 0.2s ease",
                "&:hover": { transform: "scale(1.02)" },
              }}
            >
              {/* File Icon + Name */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isImage ? (
                  <MuiIcons.Image color="primary" />
                ) : isPdf ? (
                  <MuiIcons.PictureAsPdf color="error" />
                ) : (
                  <MuiIcons.Description color="action" />
                )}
                {file.file_name}
              </Typography>

             {/* Image Thumbnail */}
{isImage && fileUrl && (
  <Box
    onClick={() => setPreviewImage(fileUrl)}
    sx={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      mt: 1,
      cursor: "pointer",
    }}
  >
    <Box
      sx={{
        width: 200,          // fixed medium width
        height: 140,         // fixed medium height
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid #ccc",
        boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
        transition: "transform 0.2s ease",
        "&:hover": { transform: "scale(1.03)" },
      }}
    >
      <img
        src={fileUrl}
        alt={file.file_name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",   // maintains aspect ratio while filling the box
        }}
      />
    </Box>
  </Box>
)}

              {/* Non-image placeholder */}
              {!isImage && (
                <Box
                  sx={{
                    width: "100%",
                    height: 120,
                    borderRadius: "8px",
                    border: "1px dashed #ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#888",
                    fontSize: "13px",
                  }}
                >
                  Preview not available
                </Box>
              )}

              {/* Action buttons */}
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {fileUrl && (
                  <IconButton
                    component="a"
                    href={fileUrl}
                    target="_blank"
                    title="Open file"
                    color="primary"
                    size="small"
                  >
                    <MuiIcons.OpenInNew fontSize="small" />
                  </IconButton>
                )}
                {downloadUrl && (
                  <IconButton
                    component="a"
                    href={downloadUrl}
                    title="Download"
                    color="success"
                    size="small"
                  >
                    <MuiIcons.Download fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Upload date */}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: "text.secondary",
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                📅{" "}
                {file.uploaded_at
                  ? new Date(file.uploaded_at).toLocaleString()
                  : "Unknown"}
              </Typography>
            </Card>
          );
        })}
      </Box>
    )}
  </Card>
</Grid>

{/* 🖼️ Modal Preview for Work Order Files */}
<Dialog
  open={!!previewImage}
  onClose={() => setPreviewImage(null)}
  maxWidth="md"
>
  <DialogContent sx={{ position: "relative", textAlign: "center" }}>
    <IconButton
      onClick={() => setPreviewImage(null)}
      sx={{ position: "absolute", top: 8, right: 8 }}
    >
      <CloseIcon />
    </IconButton>
    {previewImage && (
      <img
        src={previewImage}
        alt="Preview"
        style={{
          width: "100%",
          maxHeight: "90vh",
          objectFit: "contain",
          borderRadius: 8,
        }}
      />
    )}
  </DialogContent>
</Dialog>


        </Grid>
        <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md">
  <DialogContent sx={{ position: "relative", textAlign: "center" }}>
    <IconButton
      onClick={() => setOpenImageModal(false)}
      sx={{ position: "absolute", top: 8, right: 8 }}
    >
      <CloseIcon />
    </IconButton>
    <img
      src={imageURL}
      alt="Enlarged Attachment"
      style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }}
    />
  </DialogContent>
</Dialog>

      </CardContent>
      
    </Card>
  );
};

export default DetailsTab;